/**
 * @format
 */
import { Inject, Injectable } from '@angular/core';
import {
    EducationOptionStudyRightState,
    GenericPlanEducationOption,
    getModulePlanStateLabel,
    ModuleContentApprovalPlanState,
    ModulePlanStateLabel,
    PlanStudyRightService,
    PlanValidationResult,
    PlanValidationTs,
    RuleValidationResult,
    ValidatablePlan,
} from 'common-typescript';
import {
    Attainment,
    CourseUnit,
    CourseUnitSelection,
    CustomModuleAttainment,
    CustomStudyDraft,
    Education,
    EntityWithRule,
    Grade,
    GradeScale,
    LocalDateString,
    LocalId,
    Module,
    OtmId,
    PlanValidationState,
    StudyRight,
    SubstituteFor,
} from 'common-typescript/types';
import * as _ from 'lodash-es';
import { COLOR_SERVICE, COMMON_MODULE_SERVICE, VALID_ATTAINMENT_FILTER_SERVICE } from '../ajs-upgraded-modules';
import { CommonGradeAverageService } from './common-grade-average.service';
import { getGrade, getGradeAverage } from './plan-grades';

export interface PlanData {
    education: Education;
    modulesById: { [id: string]: Module };
    courseUnitsById: { [id: string]: CourseUnit };
    attainmentsById: { [id: string]: Attainment };
    customStudyDraftsById: { [id: string]: CustomStudyDraft };
}

/**
 * The fields of a plan validation result that plan state consumers read. The validator adds more, see RuleContext.getResults.
 * Every field is optional because a module that has an attainment but no validation result gets only state and credits.
 */
export interface PlanItemValidationResult {
    state?: `${PlanValidationState}`;
    plannedCredits?: { min: number; max: number | null };
    attainedCredits?: number;
    implicitCourseUnitIds?: OtmId[];
    invalidSelection?: boolean;
    invalidAccordingToModuleContentApproval?: boolean;
}

export interface ModuleValidationResult extends PlanItemValidationResult {
    isModuleContentApproved?: boolean;
    hasInvalidOrMissingSelections?: boolean;
}

/** The validator's results of the rules of a module or education, by the rule's local id */
export type RuleValidationResultsByLocalId = { [ruleLocalId: LocalId]: RuleValidationResult };

/**
 * How a selection relates to the module content application of its parent: the parent has none, the selection matches it, or the selection
 * differs from it while the application is only applied for (requested or in handling) or already approved (accepted or conditionally).
 * The values follow the UI: "Ei vastaa haettua sisältöä." and "Ei vastaa hyväksyttyä sisältöä."
 */
export type ModuleContentApprovalStatus = 'NONE' | 'MATCHES' | 'DIFFERS_FROM_APPLIED' | 'DIFFERS_FROM_APPROVED';

export interface SelectionValidationState {
    invalidSelection?: boolean;
    moduleContentApproval?: ModuleContentApprovalStatus;
    /** The selection is invalid, and no module content approval of the parent allows it */
    invalidSelectionWithoutApproval?: boolean;
    /** @deprecated Read moduleContentApproval. Only the old plan UI in lib/plan still reads this. */
    hasModuleContentApproval?: boolean;
    /** @deprecated Read moduleContentApproval. Only the old plan UI in lib/plan still reads this. */
    invalidSelectionAccordingToModuleContentApproval?: boolean;
    /** @deprecated Read moduleContentApproval. Only the old plan UI in lib/plan still reads this. */
    isInvalidAccordingToModuleContentApprovalRequested?: boolean;
    /** @deprecated Read moduleContentApproval. Only the old plan UI in lib/plan still reads this. */
    isInvalidAccordingToModuleContentApprovalAccepted?: boolean;
}

/** The selection breaks the module content approval of its parent, whether the approval is only requested or already accepted */
export function breaksModuleContentApproval(state: SelectionValidationState | undefined): boolean {
    return state?.moduleContentApproval === 'DIFFERS_FROM_APPLIED' || state?.moduleContentApproval === 'DIFFERS_FROM_APPROVED';
}

/** What PlanStateService.getPlanStateAndData builds the plan state and plan data from */
export interface PlanStateInput {
    education: Education;
    validatablePlan: ValidatablePlan;
    planValidationResult: PlanValidationResult;
    educationOptions: GenericPlanEducationOption[];
    gradeScalesById: { [id: OtmId]: GradeScale };
    studyRight: StudyRight;
    /** Pairs of a course unit in the plan and its newer version */
    outDatedCourseUnitsInPlan?: [CourseUnit, CourseUnit][];
}

/** The ids of what the plan selects under an education, a module or a custom module attainment */
export interface ChildSelections {
    selectedCourseUnitIds: OtmId[];
    selectedModuleIds: OtmId[];
    selectedCustomCourseUnitAttainmentIds: OtmId[];
    selectedCustomModuleAttainmentIds: OtmId[];
    selectedCustomStudyDraftIds: LocalId[];
}

export interface EducationStateObject extends ChildSelections {
    type: 'EducationStateObject';
    educationValidationResult: PlanItemValidationResult;
    ruleValidationResults: RuleValidationResultsByLocalId;
    implicitCourseUnitIds: OtmId[];
    elementId: string;
}

export interface ModuleStateObject extends SelectionValidationState, ChildSelections {
    type: 'ModuleStateObject';
    /** Whether the module has an attainment */
    isAttained: boolean;
    studyRightState: EducationOptionStudyRightState | null;
    attainmentId: OtmId;
    grade?: Grade;
    attainmentExpiryDate: LocalDateString;
    isAttainmentAboutToExpire: boolean;
    selectedParentModuleId: OtmId;
    moduleValidationResult: ModuleValidationResult;
    ruleValidationResults: RuleValidationResultsByLocalId;
    colorCategoryCssClass: string;
    /** ATTAINED when the module is attained, otherwise its validation state */
    validationState?: PlanValidationState;
    /**
     * Where the module stands with its own module content approval. (`moduleContentApproval` tells whether the module, as a selection,
     * follows the approval of its parent.)
     */
    contentApprovalState?: ModuleContentApprovalPlanState;
    /** The plan state for display, see getModulePlanStateLabel */
    planState?: ModulePlanStateLabel;
    implicitCourseUnitIds?: OtmId[];
    gradeAverage?: number;
    isInPlan: boolean;
    elementId: string;
}

export interface CourseUnitStateObject extends SelectionValidationState {
    type: 'CourseUnitStateObject';
    attainmentId: OtmId;
    grade?: Grade;
    attainmentExpiryDate: LocalDateString;
    isAttainmentAboutToExpire: boolean;
    selectedParentModuleId: OtmId;
    courseUnitValidationResult?: PlanItemValidationResult;
    colorCategoryCssClass: string;
    isSubstituted: boolean;
    substitutedBy: OtmId[];
    substituteFor: SubstituteFor[];
    isInPlan: boolean;
    isInPlanAsSubstitute: boolean;
    newerVersionAvailable?: boolean;
    selectedAssessmentItemIds: OtmId[];
    courseUnitSelection: CourseUnitSelection;
}

export interface CustomModuleAttainmentStateObject extends SelectionValidationState, Omit<ChildSelections, 'selectedCustomStudyDraftIds'> {
    type: 'CustomModuleAttainmentStateObject';
    grade: Grade;
    attainmentExpiryDate: LocalDateString;
    isAttainmentAboutToExpire: boolean;
    selectedParentModuleId: OtmId;
    customModuleAttainmentValidationResult?: ModuleValidationResult;
    colorCategoryCssClass: string;
    /** ATTAINED when the module is attained, otherwise its validation state */
    validationState?: PlanValidationState;
    /**
     * Where the module stands with its own module content approval. (`moduleContentApproval` tells whether the module, as a selection,
     * follows the approval of its parent.)
     */
    contentApprovalState?: ModuleContentApprovalPlanState;
    /** The plan state for display, see getModulePlanStateLabel */
    planState?: ModulePlanStateLabel;
    gradeAverage: number;
    isInPlan: boolean;
}

export interface CustomCourseUnitAttainmentStateObject extends SelectionValidationState {
    type: 'CustomCourseUnitAttainmentStateObject';
    grade: Grade;
    attainmentExpiryDate: LocalDateString;
    isAttainmentAboutToExpire: boolean;
    selectedParentModuleId: OtmId;
    customCourseUnitAttainmentValidationResult?: PlanItemValidationResult;
    colorCategoryCssClass: string;
    isInPlan: boolean;
}

export interface PlanStateAndData {
    planData: PlanData;
    planStateObject: PlanStateObject;
}

export interface PlanStateObject {
    education: EducationStateObject;
    modules: { [id: string]: ModuleStateObject };
    courseUnits: { [id: string]: CourseUnitStateObject };
    customModuleAttainments: { [id: string]: CustomModuleAttainmentStateObject };
    customCourseUnitAttainments: { [id: string]: CustomCourseUnitAttainmentStateObject };
    studyRight: StudyRight;
}

@Injectable({ providedIn: 'root' })
export class PlanStateService {
    constructor(
        @Inject(VALID_ATTAINMENT_FILTER_SERVICE) private validAttainmentFilterService: any,
        @Inject(COLOR_SERVICE) private colorService: any,
        private commonGradeAverageService: CommonGradeAverageService,
        @Inject(COMMON_MODULE_SERVICE) private commonModuleService: any,
    ) {}
    versionChanged = false;

    getPlanStateAndData({
        education,
        validatablePlan,
        planValidationResult,
        educationOptions,
        gradeScalesById,
        studyRight,
        outDatedCourseUnitsInPlan,
    }: PlanStateInput): PlanStateAndData {
        const planStateObject: PlanStateObject = {
            education: this.getEducationStateObject(education, validatablePlan, planValidationResult),
            modules: this.getModuleStates(validatablePlan, educationOptions, planValidationResult, gradeScalesById),
            courseUnits: this.getCourseUnitStateObject(validatablePlan, planValidationResult, gradeScalesById, outDatedCourseUnitsInPlan),
            customModuleAttainments: this.getCustomModuleAttainmentStateObject(validatablePlan, planValidationResult, gradeScalesById),
            customCourseUnitAttainments: this.getCustomCourseUnitAttainmentStateObject(
                validatablePlan,
                planValidationResult,
                gradeScalesById,
            ),
            studyRight,
        };
        return {
            planData: this.getPlanData(education, validatablePlan),
            planStateObject,
        };
    }

    private getPlanData(education: Education, validatablePlan: ValidatablePlan): PlanData {
        const modules = _.values(validatablePlan.modulesById) as Module[];
        const courseUnits = _.values(validatablePlan.courseUnitsById);
        const attainments = [
            ...modules.map(module => validatablePlan.getModuleAttainment(module.id)),
            ...courseUnits.map(courseUnit => validatablePlan.getCourseUnitAttainment(courseUnit.id)),
            ..._.values(validatablePlan.customModuleAttainmentsById),
            ..._.values(validatablePlan.customCourseUnitAttainmentsById),
        ].filter(Boolean);
        return {
            education,
            modulesById: _.keyBy(modules, 'id'),
            courseUnitsById: _.keyBy(courseUnits, 'id'),
            attainmentsById: _.keyBy(attainments, 'id'),
            customStudyDraftsById: _.keyBy(validatablePlan.plan.customStudyDrafts || [], 'id'),
        };
    }

    private getEducationStateObject(
        education: Education,
        validatablePlan: ValidatablePlan,
        planValidationResult: PlanValidationResult,
    ): EducationStateObject {
        let childModules = validatablePlan.getSelectedModulesUnderModule(education);
        if (_.every(childModules, { type: 'DegreeProgramme' })) {
            childModules = this.commonModuleService.getDegreeProgrammesOrderedByTypeUrn(childModules);
        }
        return {
            type: 'EducationStateObject',
            educationValidationResult: planValidationResult.rootValidationResult,
            ruleValidationResults: planValidationResult.ruleValidationResults?.[education.id] ?? {},
            selectedCourseUnitIds: _.map(validatablePlan.getSelectedCourseUnitsUnderModule(education), 'id'),
            selectedModuleIds: _.map(childModules, 'id'),
            selectedCustomCourseUnitAttainmentIds: _.uniq(validatablePlan.getSelectedCustomCourseUnitAttainmentIdsUnderModule(education)),
            selectedCustomModuleAttainmentIds: _.uniq(validatablePlan.getSelectedCustomModuleAttainmentIdsUnderModule(education)),
            selectedCustomStudyDraftIds: _.map(validatablePlan.getSelectedCustomStudyDraftsByParentModuleId(education.id), 'id'),
            implicitCourseUnitIds: planValidationResult.rootValidationResult?.implicitCourseUnitIds || [],
            elementId: `edu:${education.id}`,
        };
    }

    private getModuleStates(
        validatablePlan: ValidatablePlan,
        educationOptions: GenericPlanEducationOption[],
        planValidationResult: PlanValidationResult,
        gradeScalesById: { [id: string]: GradeScale },
    ): { [id: string]: ModuleStateObject } {
        const moduleStateObjectsById: { [id: string]: ModuleStateObject } = {};
        const modulesInPlan = _.values(validatablePlan.modulesById);
        _.forEach(modulesInPlan, module => {
            const moduleAttainment = validatablePlan.getModuleAttainment(module.id);
            const moduleSelection = validatablePlan.getModuleSelection(module.id);
            const selectedParentId = validatablePlan.getParentModuleOrCustomModuleAttainmentForModule(module)?.id;
            const validationResult = planValidationResult?.moduleValidationResults[module.id];
            moduleStateObjectsById[module.id] = {
                type: 'ModuleStateObject',
                studyRightState: this.getStudyRightStateForModule(module, educationOptions, validatablePlan),
                isAttained: !!moduleAttainment,
                attainmentId: moduleAttainment ? moduleAttainment.id : null,
                ...(moduleAttainment ? { grade: getGrade(moduleAttainment.gradeId, gradeScalesById[moduleAttainment.gradeScaleId]) } : {}),
                attainmentExpiryDate: moduleAttainment ? moduleAttainment.expiryDate : null,
                isAttainmentAboutToExpire: this.isAttainmentAboutToExpire(moduleAttainment, validatablePlan),
                selectedParentModuleId: _.get(moduleSelection, 'parentModuleId'),
                ruleValidationResults: planValidationResult?.ruleValidationResults?.[module.id] ?? {},
                ...(validationResult
                    ? {
                          moduleValidationResult: validationResult,
                          ...this.getModulePlanStateFields(module, validatablePlan, planValidationResult),
                          implicitCourseUnitIds: validationResult.implicitCourseUnitIds || [],
                          ...this.getSelectionValidationState(validationResult, selectedParentId, planValidationResult),
                      }
                    : {
                          moduleValidationResult: moduleAttainment
                              ? {
                                    state: 'ATTAINED',
                                    plannedCredits: { min: moduleAttainment.credits, max: moduleAttainment.credits },
                                    attainedCredits: moduleAttainment.credits,
                                }
                              : {},
                      }),
                colorCategoryCssClass: `cu-color-${this.colorService.getModuleColorCategory(validatablePlan, module.id)}`,
                selectedCourseUnitIds: _.map(validatablePlan.getSelectedCourseUnitsUnderModule(module), 'id'),
                selectedModuleIds: _.map(validatablePlan.getSelectedModulesUnderModule(module), 'id'),
                selectedCustomCourseUnitAttainmentIds: _.uniq(validatablePlan.getSelectedCustomCourseUnitAttainmentIdsUnderModule(module)),
                selectedCustomModuleAttainmentIds: _.uniq(validatablePlan.getSelectedCustomModuleAttainmentIdsUnderModule(module)),
                selectedCustomStudyDraftIds: _.map(validatablePlan.getSelectedCustomStudyDraftsByParentModuleId(module.id), 'id'),
                ...(module.type !== 'GroupingModule'
                    ? {
                          gradeAverage: getGradeAverage(
                              this.commonGradeAverageService,
                              module,
                              moduleAttainment,
                              validatablePlan,
                              gradeScalesById['sis-0-5'],
                          ),
                      }
                    : {}),
                isInPlan: validatablePlan.isModuleInPlan(module.id),
                elementId: `mod:${module.id}`,
            };
        });
        return moduleStateObjectsById;
    }

    private getCourseUnitStateObject(
        validatablePlan: ValidatablePlan,
        planValidationResult: PlanValidationResult,
        gradeScalesById: { [id: string]: GradeScale },
        outDatedCourseUnitsInPlan?: [CourseUnit, CourseUnit][],
    ): { [id: string]: CourseUnitStateObject } {
        const courseUnitStatesById: { [id: string]: CourseUnitStateObject } = {};
        const courseUnitsInPlan = _.values(validatablePlan.courseUnitsById);
        _.forEach(courseUnitsInPlan, courseUnit => {
            const courseUnitAttainment = validatablePlan.getCourseUnitAttainment(courseUnit.id);
            const courseUnitSelection = validatablePlan.getCourseUnitSelection(courseUnit.id) as CourseUnitSelection;
            const selectedParentModuleId = _.get(courseUnitSelection, 'parentModuleId');
            const validationResult = planValidationResult?.courseUnitValidationResults[courseUnit.id];
            courseUnitStatesById[courseUnit.id] = {
                type: 'CourseUnitStateObject',
                attainmentId: courseUnitAttainment ? courseUnitAttainment.id : null,
                ...(courseUnitAttainment
                    ? { grade: getGrade(courseUnitAttainment.gradeId, gradeScalesById[courseUnitAttainment.gradeScaleId]) }
                    : {}),
                attainmentExpiryDate: courseUnitAttainment ? courseUnitAttainment.expiryDate : null,
                isAttainmentAboutToExpire: this.isAttainmentAboutToExpire(courseUnitAttainment, validatablePlan),
                selectedParentModuleId,
                ...(validationResult
                    ? {
                          courseUnitValidationResult: validationResult,
                          ...this.getSelectionValidationState(validationResult, selectedParentModuleId, planValidationResult),
                      }
                    : {}),
                colorCategoryCssClass: `cu-color-${this.colorService.getCourseUnitColorCategory(validatablePlan, courseUnit.id)}`,
                isInPlan: validatablePlan.isCourseUnitInPlan(courseUnit),
                isInPlanAsSubstitute: validatablePlan.isCourseUnitInPlanAsSubstitute(courseUnit),
                isSubstituted: !_.isEmpty(_.get(courseUnitSelection, 'substitutedBy')),
                substitutedBy: _.get(courseUnitSelection, 'substitutedBy'),
                substituteFor: _.get(courseUnitSelection, 'substituteFor'),
                newerVersionAvailable: _.some(outDatedCourseUnitsInPlan, outDatedCourseUnit => outDatedCourseUnit[0].id === courseUnit.id),
                selectedAssessmentItemIds: _.map(validatablePlan.getSelectedAssessmentItems(courseUnit), ai => ai.id),
                courseUnitSelection,
            };
        });
        return courseUnitStatesById;
    }

    private getCustomModuleAttainmentStateObject(
        validatablePlan: ValidatablePlan,
        planValidationResult: PlanValidationResult,
        gradeScalesById: { [id: string]: GradeScale },
    ): { [id: string]: CustomModuleAttainmentStateObject } {
        const customModuleAttainmentStatesById: { [id: string]: CustomModuleAttainmentStateObject } = {};
        const customModuleAttainmentsPlan = _.values(validatablePlan.customModuleAttainmentsById);
        _.forEach(customModuleAttainmentsPlan, customModuleAttainment => {
            const selection = validatablePlan.customModuleAttainmentIdSelectionMap[customModuleAttainment.id];
            const selectedParentId = validatablePlan.getParentModuleOrCustomModuleAttainmentForCustomAttainment(customModuleAttainment)?.id;
            const validationResult = planValidationResult?.customModuleAttainmentValidationResults[customModuleAttainment.id];
            customModuleAttainmentStatesById[customModuleAttainment.id] = {
                type: 'CustomModuleAttainmentStateObject',
                grade: getGrade(customModuleAttainment.gradeId, gradeScalesById[customModuleAttainment.gradeScaleId]),
                attainmentExpiryDate: customModuleAttainment.expiryDate,
                isAttainmentAboutToExpire: this.isAttainmentAboutToExpire(customModuleAttainment, validatablePlan),
                selectedParentModuleId: _.get(selection, 'parentModuleId'),
                ...(validationResult
                    ? {
                          customModuleAttainmentValidationResult: validationResult,
                          ...this.getModulePlanStateFields(customModuleAttainment, validatablePlan, planValidationResult),
                          ...this.getSelectionValidationState(validationResult, selectedParentId, planValidationResult),
                      }
                    : {}),
                colorCategoryCssClass: `cu-color-${this.colorService.getModuleColorCategory(validatablePlan, customModuleAttainment.id)}`,
                selectedCourseUnitIds: _.map(
                    validatablePlan.getSelectedCourseUnitsUnderCustomModuleAttainment(customModuleAttainment),
                    'id',
                ),
                selectedModuleIds: _.map(validatablePlan.getSelectedModulesUnderCustomModuleAttainment(customModuleAttainment), 'id'),
                selectedCustomCourseUnitAttainmentIds: _.map(
                    _.uniq(validatablePlan.getSelectedCustomCourseUnitAttainmentsUnderCustomModuleAttainment(customModuleAttainment)),
                    'id',
                ),
                selectedCustomModuleAttainmentIds: _.map(
                    _.uniq(validatablePlan.getSelectedCustomModuleAttainmentsUnderCustomModuleAttainment(customModuleAttainment)),
                    'id',
                ),
                gradeAverage: getGradeAverage(
                    this.commonGradeAverageService,
                    null,
                    customModuleAttainment,
                    validatablePlan,
                    gradeScalesById['sis-0-5'],
                ),
                isInPlan: validatablePlan.isModuleAttainmentInPlan(customModuleAttainment.id),
            };
        });
        return customModuleAttainmentStatesById;
    }

    private getCustomCourseUnitAttainmentStateObject(
        validatablePlan: ValidatablePlan,
        planValidationResult: PlanValidationResult,
        gradeScalesById: { [id: string]: GradeScale },
    ): { [id: string]: CustomCourseUnitAttainmentStateObject } {
        const customCourseUnitAttainmentStatesById: { [id: string]: CustomCourseUnitAttainmentStateObject } = {};
        const customCourseUnitAttainmentsInPlan = _.values(validatablePlan.customCourseUnitAttainmentsById);
        _.forEach(customCourseUnitAttainmentsInPlan, customCourseUnitAttainment => {
            const selection = validatablePlan.customCourseUnitAttainmentIdSelectionMap[customCourseUnitAttainment.id];
            const selectedParentModuleId = _.get(selection, 'parentModuleId');
            const validationResult = planValidationResult?.customCourseUnitAttainmentValidationResults[customCourseUnitAttainment.id];
            customCourseUnitAttainmentStatesById[customCourseUnitAttainment.id] = {
                type: 'CustomCourseUnitAttainmentStateObject',
                grade: getGrade(customCourseUnitAttainment.gradeId, gradeScalesById[customCourseUnitAttainment.gradeScaleId]),
                attainmentExpiryDate: customCourseUnitAttainment.expiryDate || null,
                isAttainmentAboutToExpire: this.isAttainmentAboutToExpire(customCourseUnitAttainment, validatablePlan),
                selectedParentModuleId,
                ...(validationResult
                    ? {
                          customCourseUnitAttainmentValidationResult: validationResult,
                          ...this.getSelectionValidationState(validationResult, selectedParentModuleId, planValidationResult),
                      }
                    : {}),
                colorCategoryCssClass: `cu-color-${this.colorService.getCourseUnitColorCategory(validatablePlan, customCourseUnitAttainment.id)}`,
                isInPlan: validatablePlan.isCustomCourseUnitAttainmentInPlan(customCourseUnitAttainment),
            };
        });
        return customCourseUnitAttainmentStatesById;
    }

    private getStudyRightStateForModule(
        module: EntityWithRule,
        educationOptions: GenericPlanEducationOption[],
        validatablePlan: ValidatablePlan,
    ): EducationOptionStudyRightState | null {
        if (!module || !educationOptions) {
            return null;
        }
        const matchingEducationOption = PlanStudyRightService.getMatchingEducationOption(module.groupId, educationOptions, validatablePlan);
        if (!!matchingEducationOption && matchingEducationOption.isInPlan && !matchingEducationOption.isInPlanAsMinor) {
            return matchingEducationOption.studyRightState;
        }
        return null;
    }

    private isAttainmentAboutToExpire(attainment: Attainment, validatablePlan: ValidatablePlan): boolean {
        if (
            attainment &&
            attainment.expiryDate &&
            this.validAttainmentFilterService.isAttainmentAboutToExpire(attainment) &&
            !this.validAttainmentFilterService.isAttached(attainment, validatablePlan.getAllAttainments())
        ) {
            return true;
        }
        return false;
    }

    private getModulePlanStateFields(
        module: EntityWithRule | CustomModuleAttainment,
        validatablePlan: ValidatablePlan,
        planValidationResult: PlanValidationResult,
    ): Pick<ModuleStateObject, 'validationState' | 'contentApprovalState' | 'planState'> {
        const modulePlanState = PlanValidationTs.getModulePlanState(module, validatablePlan, planValidationResult);
        return modulePlanState
            ? {
                  validationState: modulePlanState.validationState,
                  contentApprovalState: modulePlanState.contentApproval,
                  planState: getModulePlanStateLabel(modulePlanState),
              }
            : { validationState: null, contentApprovalState: null, planState: null };
    }

    private getSelectionValidationState(
        validationResult: PlanItemValidationResult,
        parentId: OtmId,
        planValidationResult: PlanValidationResult,
    ): SelectionValidationState {
        const moduleContentApproval = this.getModuleContentApprovalStatus(validationResult, parentId, planValidationResult);
        return {
            invalidSelection: validationResult.invalidSelection,
            moduleContentApproval,
            invalidSelectionWithoutApproval: !!validationResult.invalidSelection && moduleContentApproval === 'NONE',
            hasModuleContentApproval: validationResult.invalidAccordingToModuleContentApproval !== undefined,
            invalidSelectionAccordingToModuleContentApproval: validationResult.invalidAccordingToModuleContentApproval,
            isInvalidAccordingToModuleContentApprovalRequested:
                validationResult.invalidAccordingToModuleContentApproval && !this.isModuleContentApproved(parentId, planValidationResult),
            isInvalidAccordingToModuleContentApprovalAccepted:
                validationResult.invalidAccordingToModuleContentApproval && this.isModuleContentApproved(parentId, planValidationResult),
        };
    }

    private getModuleContentApprovalStatus(
        validationResult: PlanItemValidationResult,
        parentId: OtmId,
        planValidationResult: PlanValidationResult,
    ): ModuleContentApprovalStatus {
        if (validationResult.invalidAccordingToModuleContentApproval === undefined) {
            return 'NONE';
        }
        if (!validationResult.invalidAccordingToModuleContentApproval) {
            return 'MATCHES';
        }
        return this.isModuleContentApproved(parentId, planValidationResult) ? 'DIFFERS_FROM_APPROVED' : 'DIFFERS_FROM_APPLIED';
    }

    private isModuleContentApproved(parentId: OtmId, planValidationResult: PlanValidationResult) {
        return planValidationResult.moduleValidationResults[parentId].isModuleContentApproved;
    }
}
